import React, { useState, useEffect, ReactNode } from 'react';
import { KeyboardContext, type KeyboardContextValue } from './KeyboardContextCore';

interface KeyboardProviderProps {
  children: ReactNode;
  isKioskClient: boolean; // Bestimmt, ob Tastatur überhaupt aktiviert wird
}

export function KeyboardProvider({
  children,
  isKioskClient
}: KeyboardProviderProps) {
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [focusedElement, setFocusedElement] =
    useState<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // NEU: Flags und Setter für Passwort-Sonderbehandlung
  const [isPasswordField, setIsPasswordField] = useState(false);
  const [passwordSetter, setPasswordSetter] = useState<
    React.Dispatch<React.SetStateAction<string>> | undefined
  >(undefined);

  useEffect(() => {
    // TESTZWECKE: Tastatur ist immer aktiv (auch auf normalen PCs)
    // Original: 
    if (!isKioskClient) return;
    
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      
      // Prüfen, ob es ein INPUT oder TEXTAREA ist
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        const inputElement = target as HTMLInputElement | HTMLTextAreaElement;
        
        // WICHTIG: Nur Text-Input-Felder sollen die Tastatur auslösen
        // Ausschließen: checkbox, radio, button, submit, hidden, file, etc.
        if (target.tagName === 'INPUT') {
          const inputType = (target as HTMLInputElement).type.toLowerCase();
          const allowedTypes = ['text', 'password', 'email', 'search', 'tel', 'url', 'number'];
          
          // Wenn es kein erlaubter Input-Typ ist, ignorieren
          if (!allowedTypes.includes(inputType)) {
            return;
          }
        }
        
        // Zusätzliche Prüfung: Element muss editierbar sein
        if (inputElement.readOnly || inputElement.disabled) {
          return;
        }
        
        // Prüfen, ob das Element in einem Canvas-Container oder SignatureCanvas ist
        let parent = target.parentElement;
        let depth = 0;
        while (parent && depth < 5) {
          // Canvas-Container oder Signature-bezogene Klassen ausschließen
          if (
            parent.tagName === 'CANVAS' ||
            parent.classList.contains('signature-canvas') ||
            parent.classList.contains('sigCanvas') ||
            parent.id?.includes('signature')
          ) {
            return; // Tastatur NICHT anzeigen
          }
          parent = parent.parentElement;
          depth++;
        }
        
        setKeyboardVisible(true);
        setFocusedElement(inputElement);

        // Prüfen, ob es ein Passwortfeld ist
        if (
          target.tagName === 'INPUT' &&
          (target as HTMLInputElement).type === 'password'
        ) {
          setIsPasswordField(true);
        } else {
          setIsPasswordField(false);
        }

        // Wenn du KEINE HW-Tastatur-Eingabe erlauben willst,
        // kannst du hier optional inputmode="none" / readonly einschalten
        // (target as HTMLInputElement).setAttribute("inputmode", "none");
        // (target as HTMLInputElement).setAttribute("readonly", "true");
      }
    };

    const handleFocusOut = () => {
      // Wir warten minimal, um zu erkennen, ob ein anderes Input direkt Fokus bekommt
      setTimeout(() => {
        const active = document.activeElement;
        if (
          !active ||
          (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')
        ) {
          // Tastatur ausblenden
          setKeyboardVisible(false);
          setFocusedElement(null);
          setIsPasswordField(false);
        }
      }, 0);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isKioskClient]);

  // Manuelle Methode, um Tastatur zu schließen
  const hideKeyboard = () => {
    setKeyboardVisible(false);
    if (focusedElement) {
      focusedElement.blur();
      setFocusedElement(null);
    }
    setIsPasswordField(false);
  };

  const value: KeyboardContextValue = {
    keyboardVisible,
    focusedElement,
    hideKeyboard,
    isPasswordField,
    setIsPasswordField,
    passwordSetter,
    setPasswordSetter
  };

  return (
    <KeyboardContext.Provider value={value}>
      {children}
    </KeyboardContext.Provider>
  );
}
